// © Microsoft Corporation. All rights reserved.

import { ColumnArrangement, getMediaQuerySelector, getMediaQuerySelectorFor } from "@msnews/experiences-base";
import { DesignSystem, highContrastBorder, isDarkMode, neutralForegroundRest } from "@microsoft/fast-components-styles-msft";

import { ComponentStyles } from "@microsoft/fast-jss-manager";
import { CommonHeaderClassNameContract } from "./CommonHeader.classnames";
import { ZIndex } from "@msnews/core";
import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";

/**
 * The Common header styles
 */
export const CommonHeaderStyles: ComponentStyles<CommonHeaderClassNameContract, DesignSystem> = (config: DesignSystem) => {
    return {
        contextualNavContainer: {
            "flex-grow": "1",
            width: "calc(100% - 44px)"
        },
        contextualSecondaryNavContainer: {
            "align-items": "center",
            display: "flex",
            "flex-direction": "row",
            height: "46px",
            "grid-column": "1",
            width: "inherit",
            [getMediaQuerySelector(null, ColumnArrangement.c2)]: {
                "grid-row": "4"
            },
            [getMediaQuerySelector(ColumnArrangement.c3, null)]: {
                "grid-row": "3"
            }
        },
        secondaryFeedNavParent: {
            width: "100%"
        },
        hamburgerMenuContainer: {
            "margin-inline-start": "-8px",
            "margin-inline-end": "12px",
            "min-width": "32px",
            position: "relative",
            "z-index": `${ZIndex.Nav}`
        },
        languageSelector: {
            "margin-top": "12px"
        },
        logo: {
            "margin-inline-end": "15px",
            "& svg path:first-child": {
                fill: "var(--common-header-logo-color)"
            },
            "grid-row": "1",
            "grid-column": "1"
        },
        userPreferencesContainer: {
            width: "92px",
            display: "flex",
            "flex-direction": "row",
            "align-items": "center",
            "justify-content": "flex-end",
            "grid-row": "1",
            "grid-column": "3",
            "justify-self": "end",
            "margin-inline-end": "-8px",
            "min-height": "48px"
        },
        navigationContainer: {
            "align-items": "center",
            display: "flex",
            "flex-direction": "row",
            height: "46px",
            "grid-column": "1",
            [getMediaQuerySelector(null, ColumnArrangement.c2)]: {
                "grid-row": "3"
            },
            [getMediaQuerySelector(ColumnArrangement.c3, null)]: {
                "grid-row": "2"
            },
            width: "inherit"
        },
        root: {
            width: "100%"
        },
        rootContainer: {
            display: "grid",
            margin: "auto",
            [getMediaQuerySelectorFor(ColumnArrangement.c1)]: {
                width: "343px",
                "grid-template-rows": "repeat(3, auto)"
            },
            [getMediaQuerySelectorFor(ColumnArrangement.c2)]: {
                width: "612px",
                "grid-template-rows": "repeat(3, auto)"
            },
            [getMediaQuerySelectorFor(ColumnArrangement.c3)]: {
                width: "924px",
                "grid-template-rows": "repeat(2, auto)"
            },
            [getMediaQuerySelectorFor(ColumnArrangement.c4)]: {
                width: "1236px",
                "grid-template-rows": "repeat(2, auto)"
            },
            "@media (min-width: 1920px)": {
                width: "1790px",
                "grid-template-rows": "repeat(2, auto)"
            }
        },
        topContainer: {
            "align-items": "center",
            display: "grid",
            "margin-bottom": "10px",
            "grid-column": "1",
            "grid-gap": "10px",
            "padding-top": "10px",
            "justify-content": "space-between",
            [getMediaQuerySelector(null, ColumnArrangement.c1)]: {
                "grid-row": "1",
                "grid-template": "repeat(2, auto) / repeat(3, auto)",
                width: "inherit"
            },
            [getMediaQuerySelector(ColumnArrangement.c1, ColumnArrangement.c2)]: {
                "grid-row": "1",
                "grid-template": "repeat(2, auto) / repeat(3, auto)"
            },
            [getMediaQuerySelector(ColumnArrangement.c3, null)]: {
                "grid-row": "1",
                "grid-template": "auto / repeat(3, auto)"
            }
        },
        searchContainer: {
            height: "44px",
            "& form #sw_as": {
                "& .sa_as": {
                    "border-radius": "0 0 4px 4px",
                    "box-shadow": "0 .6px 1.8px rgba(0, 0, 0, .11), 0 3.2px 7.2px rgba(0, 0, 0, .13)",
                    ...highContrastBorder,
                    [getMediaQuerySelectorFor(ColumnArrangement.c1)]: {
                        width: "343px"
                    },
                    [getMediaQuerySelectorFor(ColumnArrangement.c2)]: {
                        width: "612px"
                    },
                    [getMediaQuerySelectorFor(ColumnArrangement.c3)]: {
                        width: "600px"
                    },
                    [getMediaQuerySelector(ColumnArrangement.c4, null)]: {
                        width: "780px"
                    }
                },
                clear: "both",
                position: "relative",
                "z-index": `${ZIndex.Overlay}`,
                // These properties are used to align the autosuggest container with the search box
                [applyLocalizedProperty("left", "right", config.direction)]: "-1px",
                top: "-6px"
            },
            "& form body.as_active:not(.nw_as_active)": {
                "border-radius": "4px 4px 0 0"
            },
            [getMediaQuerySelector(null, ColumnArrangement.c1)]: {
                "grid-row": "2",
                "grid-column": "1 / 4",
                width: "inherit"
            },
            [getMediaQuerySelector(ColumnArrangement.c1, ColumnArrangement.c2)]: {
                "grid-row": "2",
                "grid-column": "1 / 4"
            },
            [getMediaQuerySelector(ColumnArrangement.c3, null)]: {
                "grid-row": "1",
                "grid-column": "2"
            }
        },
        settingsContainer: {
            "margin-inline-start": "12px"
        }
    };
};

// Style overrides for the React Select control
export const MarketSelectorStyles = {
    select_menu: {
        "z-index": 100
    }
};